import type braintree from 'braintree-web';
import {
  type AuthorizationResponse,
  type ButtonStyle,
} from 'paypal-checkout-components';
import React, { useCallback } from 'react';

import { useFlowContext } from '@/components/flow/hooks/useFlowContext';
import { PaypalButton } from '@/components/payments/components/paypalButton';
import type { CHECKOUT_CONTEXT } from '@/consts/checkout';
import { PAYMENT_TYPES, PROCESSOR_NAMES } from '@/consts/payments';
import { PaymentMethodService } from '@/services/paymentsService/client';
import { type NewPaymentMethod } from '@/services/resubscriptionService/types';
import type { RegisterPaymentMethodData } from '@/types/payments/types';
import { AnalyticsEvent, trackEvent } from '@/utils/analytics';

/**
 * The button links the user's PayPal account and registers the payment in the backend.
 *
 * @prop onSuccess Executed on the successfully registered payment method.
 * @prop onError Executed on any error message in the workflow.
 * @prop createPaymentOptions Optional. Overrides any default options in usePaypalButton.
 * @prop buttonStyleOptions Optional. Overrides any default options in usePaypalButton.
 * @prop checkoutContext Optional. Indicates context if rendering in a checkout flow.
 */
const RegisterPaypalPaymentMethodButton: React.FC<{
  onSuccess: (paymentMethod: NewPaymentMethod) => void;
  onError: (message: string) => void;
  createPaymentOptions?: Partial<braintree.PayPalCheckoutCreatePaymentOptions>;
  buttonStyleOptions?: Partial<ButtonStyle>;
  checkoutContext?: valueof<typeof CHECKOUT_CONTEXT>;
}> = ({
  onSuccess,
  onError,
  createPaymentOptions,
  buttonStyleOptions,
  checkoutContext,
}) => {
  const { brand } = useFlowContext();
  const getPaypalPaymentMethod = (
    token: AuthorizationResponse,
  ): NewPaymentMethod => {
    return {
      customerIdentifier: token.details.email,
      processor: PROCESSOR_NAMES.BRAINTREE,
      type: PAYMENT_TYPES.PAYPAL,
    };
  };

  const formatNewPaymentMethodToData = useCallback(
    (
      newPaymentMethod: NewPaymentMethod,
      paymentNonce: string,
      deviceData: string,
    ): RegisterPaymentMethodData => {
      return {
        method: newPaymentMethod.type,
        paymentNonce,
        deviceData,
        vendor: newPaymentMethod.processor,
        ...(checkoutContext ? { checkoutContext } : {}),
      };
    },
    [checkoutContext],
  );

  const onStorePaymentSuccess = useCallback(
    (newPaymentMethod: NewPaymentMethod) => {
      trackEvent(AnalyticsEvent.PAYPAL_ACCOUNT_LINKED);
      onSuccess(newPaymentMethod);
    },
    [onSuccess],
  );

  const onStorePaymentError = useCallback(
    (message: string) => {
      trackEvent(AnalyticsEvent.PAYPAL_VALIDATION_ERROR);
      onError(message);
    },
    [onError],
  );

  const storePaypalPayment = useCallback(
    async (token: AuthorizationResponse, deviceData: string) => {
      const newPaymentMethod = getPaypalPaymentMethod(token);

      await PaymentMethodService.storePaymentMethod({
        brand,
        data: formatNewPaymentMethodToData(
          newPaymentMethod,
          token.nonce,
          deviceData,
        ),
        onSuccess: () => onStorePaymentSuccess(newPaymentMethod),
        onError: (message) => onStorePaymentError(message),
      });
    },
    [
      brand,
      formatNewPaymentMethodToData,
      onStorePaymentSuccess,
      onStorePaymentError,
    ],
  );

  return (
    <PaypalButton
      onSuccess={storePaypalPayment}
      onError={onError}
      onRenderButton={() => trackEvent(AnalyticsEvent.PAYPAL_BUTTON_LOADED)}
      createPaymentOptions={createPaymentOptions}
      buttonStyleOptions={buttonStyleOptions}
    />
  );
};

export default RegisterPaypalPaymentMethodButton;
